import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Calendar, Infinity, X, Loader2, Shield, Database, Search, Trash2, CheckSquare, Square } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getAllUsers, grantUnlimitedAccess, revokeUnlimitedAccess, getAllCachedResearch, deleteCacheEntries } from '../services/supabase';
import { cn } from '../utils/cn';

export default function AdminPage() {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [grantModal, setGrantModal] = useState(null);
  const [grantDays, setGrantDays] = useState(30);
  const [updating, setUpdating] = useState(null);

  // Cache state
  const [cacheEntries, setCacheEntries] = useState([]);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheFilter, setCacheFilter] = useState('');
  const [selectedCache, setSelectedCache] = useState(new Set());
  const [deletingCache, setDeletingCache] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
    }
  }, [isAdmin, authLoading, navigate]);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadCache();
    }
  }, [isAdmin]);

  async function loadCache(filter = '') {
    try {
      setCacheLoading(true);
      const data = await getAllCachedResearch(filter);
      setCacheEntries(data);
      setSelectedCache(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setCacheLoading(false);
    }
  }

  async function handleDeleteCache() {
    if (selectedCache.size === 0) return;

    setDeletingCache(true);
    try {
      await deleteCacheEntries(Array.from(selectedCache));
      await loadCache(cacheFilter);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingCache(false);
    }
  }

  function toggleCacheSelection(id) {
    const newSelection = new Set(selectedCache);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedCache(newSelection);
  }

  function toggleAllCache() {
    if (selectedCache.size === cacheEntries.length) {
      setSelectedCache(new Set());
    } else {
      setSelectedCache(new Set(cacheEntries.map(e => e.id)));
    }
  }

  async function loadUsers() {
    try {
      setLoading(true);
      const data = await getAllUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleGrant(userId) {
    setUpdating(userId);
    try {
      const untilDate = new Date();
      untilDate.setDate(untilDate.getDate() + grantDays);
      await grantUnlimitedAccess(userId, untilDate.toISOString());
      await loadUsers();
      setGrantModal(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  }

  async function handleRevoke(userId) {
    setUpdating(userId);
    try {
      await revokeUnlimitedAccess(userId);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  function isUnlimitedActive(user) {
    return user.unlimited_until && new Date(user.unlimited_until) > new Date();
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-secondary" />
          <h1 className="text-title font-display text-primary">Admin Panel</h1>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-error/10 border border-error text-error">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">
              Dismiss
            </button>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-card rounded-xl shadow-card overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Users className="w-5 h-5 text-muted-foreground" />
            <span className="font-medium">{users.length} Users</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-muted text-left text-sm">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">Usage</th>
                  <th className="px-4 py-3 font-medium">Unlimited Until</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {user.email}
                        {user.is_admin && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-secondary text-secondary-foreground">
                            Admin
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        "px-2 py-1 text-xs rounded-full",
                        user.subscription_tier === 'free_trial' && "bg-muted text-muted-foreground",
                        user.subscription_tier === 'pro' && "bg-secondary/10 text-secondary",
                        user.subscription_tier === 'advanced' && "bg-accent/10 text-accent",
                      )}>
                        {user.subscription_tier}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {user.requests_used} / {user.requests_limit}
                    </td>
                    <td className="px-4 py-3">
                      {isUnlimitedActive(user) ? (
                        <span className="flex items-center gap-1 text-success text-sm">
                          <Infinity className="w-4 h-4" />
                          {formatDate(user.unlimited_until)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      {isUnlimitedActive(user) ? (
                        <button
                          onClick={() => handleRevoke(user.id)}
                          disabled={updating === user.id}
                          className="px-3 py-1 text-sm rounded-lg border border-error text-error hover:bg-error/10 disabled:opacity-50"
                        >
                          {updating === user.id ? 'Revoking...' : 'Revoke'}
                        </button>
                      ) : (
                        <button
                          onClick={() => setGrantModal(user)}
                          className="px-3 py-1 text-sm rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/90"
                        >
                          Grant Unlimited
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Cache Management */}
        <div className="bg-card rounded-xl shadow-card overflow-hidden mt-8">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-muted-foreground" />
              <span className="font-medium">Research Cache</span>
              <span className="text-sm text-muted-foreground">({cacheEntries.length} entries)</span>
            </div>

            {selectedCache.size > 0 && (
              <button
                onClick={handleDeleteCache}
                disabled={deletingCache}
                className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-error text-error-foreground hover:bg-error/90 disabled:opacity-50"
              >
                {deletingCache ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete {selectedCache.size} selected
              </button>
            )}
          </div>

          {/* Filter */}
          <div className="p-4 border-b border-border">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Filter by query or market name..."
                value={cacheFilter}
                onChange={(e) => setCacheFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadCache(cacheFilter)}
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-border bg-background focus:border-secondary focus:ring-2 focus:ring-secondary/20 text-sm"
              />
            </div>
            <button
              onClick={() => loadCache(cacheFilter)}
              className="mt-2 text-sm text-secondary hover:underline"
            >
              Apply filter
            </button>
          </div>

          {cacheLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : cacheEntries.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No cached research found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted text-left text-sm">
                    <th className="px-4 py-3 font-medium w-10">
                      <button onClick={toggleAllCache} className="hover:text-primary">
                        {selectedCache.size === cacheEntries.length ? (
                          <CheckSquare className="w-4 h-4" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    </th>
                    <th className="px-4 py-3 font-medium">Query</th>
                    <th className="px-4 py-3 font-medium">Market Name</th>
                    <th className="px-4 py-3 font-medium">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {cacheEntries.map((entry) => (
                    <tr
                      key={entry.id}
                      className={cn(
                        "border-t border-border hover:bg-muted/30 cursor-pointer",
                        selectedCache.has(entry.id) && "bg-secondary/10"
                      )}
                      onClick={() => toggleCacheSelection(entry.id)}
                    >
                      <td className="px-4 py-3">
                        {selectedCache.has(entry.id) ? (
                          <CheckSquare className="w-4 h-4 text-secondary" />
                        ) : (
                          <Square className="w-4 h-4 text-muted-foreground" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm">{entry.original_query}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-muted-foreground">{entry.market_name}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(entry.updated_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Grant Modal */}
      {grantModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card rounded-xl shadow-modal p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-display font-bold">Grant Unlimited Access</h2>
              <button
                onClick={() => setGrantModal(null)}
                className="p-1 hover:bg-muted rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-muted-foreground mb-4">
              Grant unlimited access to <strong>{grantModal.email}</strong>
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Duration (days)</label>
              <div className="flex gap-2">
                {[7, 14, 30, 90].map((days) => (
                  <button
                    key={days}
                    onClick={() => setGrantDays(days)}
                    className={cn(
                      "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                      grantDays === days
                        ? "bg-secondary text-secondary-foreground"
                        : "border border-border hover:bg-muted"
                    )}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted mb-6">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm">
                Access until: {formatDate(new Date(Date.now() + grantDays * 24 * 60 * 60 * 1000))}
              </span>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setGrantModal(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={() => handleGrant(grantModal.id)}
                disabled={updating === grantModal.id}
                className="flex-1 px-4 py-2 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {updating === grantModal.id ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Granting...
                  </>
                ) : (
                  'Grant Access'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
